// src/pages/EditTeam.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import TeamForm from '../components/forms/TeamForm';
import { getTeamMemberById, updateTeamMember } from '../services/TeamService';
import { deleteImages } from '../services/UploadService';
import { ITeamMember } from '../types/Team';
import { useToast } from '../hooks/useToast'; // ✅ Importar toast
import styles from '../styles/FormPages.module.css';

const EditTeam: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<ITeamMember | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasSaved = useRef(false);
  const sessionUploads = useRef<string[]>([]);
  const toast = useToast(); // ✅ Usar toast

  useEffect(() => {
    const uploadsToClean = sessionUploads;
    const cleanupFunction = deleteImages;
    return () => {
      if (!hasSaved.current && uploadsToClean.current.length > 0) {
        console.log("Cancelando edición, limpiando imágenes:", uploadsToClean.current);
        cleanupFunction(uploadsToClean.current);
      }
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const memberId = id ? parseInt(id, 10) : undefined;
    
    const fetchMember = async () => {
      if (memberId !== undefined && !isNaN(memberId)) {
        try {
          const fetchedMember = await getTeamMemberById(memberId);
          setMember(fetchedMember);
        } catch (err: any) {
          setError(err.message);
          toast.error('Error', 'No se pudo cargar el miembro del equipo.');
        }
      } else {
        setError('ID de miembro inválido.');
        toast.error('Error', 'ID de miembro inválido.');
      }
      setLoading(false);
    };
    fetchMember();
  }, [id]);

  const handleSubmit = async (formData: ITeamMember, imagesToDelete: string[]) => {
    try {
      await updateTeamMember(formData);
      
      if (imagesToDelete.length > 0) {
        await deleteImages(imagesToDelete);
      }
      
      hasSaved.current = true;
      toast.success(
        'Miembro actualizado',
        `"${formData.firstName} ${formData.lastName}" ha sido actualizado exitosamente.`
      );
      navigate('/team');
    } catch (error: any) {
      toast.error(
        'Error al actualizar miembro',
        error.message || 'No se pudo actualizar el miembro del equipo.'
      );
    }
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Cargando...">
        <div className={styles.loadingMessage}>Cargando...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout pageTitle="Error">
        <div className={styles.loadingMessage} style={{ color: 'red' }}>Error: {error}</div>
      </AdminLayout>
    );
  }

  if (!member) {
    return (
      <AdminLayout pageTitle="Error">
        <div className={styles.loadingMessage}>Miembro no encontrado.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle={`Editar Miembro: ${member.firstName} ${member.lastName}`}>
      <div className={styles.formWrapper}>
        <div className={styles.pageHeader}>
          <h2 className={styles.title}>{`Editar Miembro: ${member.firstName} ${member.lastName}`}</h2>
          <Link to="/team" className={styles.backButton}>← Volver</Link>
        </div>
        <TeamForm 
          initialData={member} 
          onSubmit={handleSubmit} 
          isEditing={true} 
          sessionUploadsRef={sessionUploads}
        />
      </div>
    </AdminLayout>
  );
};

export default EditTeam;